import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core'
import { atom, useAtomValue } from 'jotai'
import type { Atom } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { useCallback } from 'react'
import { LOGIN_SITE, SURVEY_NOT_ANSWERED } from '~/lib/constants/constants'
import { ClientError } from '~/lib/error/clientError'
import { getFragmentData } from '~/lib/graphql'
import type {
  ReservationGivenPointsType,
  ReservationGivenPointsUsage,
  ReserveCancelPolicyInput,
  ReserveDiscountCouponInput,
  ReserveGuestAuthenticationType,
  ReserveInput,
  ReservePaymentType,
  ReservePersonalInfoInput,
  ReservePlanInput,
} from '~/lib/graphql/graphql'
import type { HtmlFragment } from '~/lib/graphql/scalars'
import { error } from '~/lib/log'
import { callRegisterMember } from '~/lib/member/member'
import { useRedirectBackUrlToReserveInput } from '~/navigation'
import { env$ } from '~/stores/env'
import { createPayjpNewCardToken$, userStatus$ } from './cancelGuard'
import { tokenErrorOnReserve$ } from './card'
import { adultCount$ } from './childMenu'
import { atomWithError, error$ } from './error'
import { RecoverableClientError } from './lib/recoverableClientError'
import {
  appliedIkyuPoints$,
  appliedPaypayPoints$,
  givenPoints$,
  offPriceByCoupon$,
  paymentAmount$,
  selectedGivenPointsUsageForInput$,
  totalAmount$,
} from './payment'
import type { Reserve } from './reserve.graphql'
import { ReserveSuccess } from './reserveSuccess.frag'
import {
  affiliateSiteFormAnswer$,
  authResult$,
  card$,
  childCount$,
  discountCouponType$,
  email$,
  isDiscountCouponEnabled$,
  isReserverDifferentFromVisitor$,
  password$,
  paymentMethod$,
  planId$,
  requestsAndComments$,
  reservationInputStateValue$,
  reserverNameKana$,
  reserverPhoneNumber$,
  restaurantId$,
  seatId$,
  selectedChildMenus$,
  selectedGivenPointsType$,
  selectedOptionMenus$,
  subscribeMailMagazineIkyuNews$,
  subscribeMailMagazinePointCoupon$,
  useSignupOnly,
  visitDate$,
  visitTime$,
  visitorNameKana$,
  visitorPhoneNumber$,
  visitorsCount$,
} from './stateMachine/context'
import type { ReserveInputPlan, ReserveInputViewer } from './stateMachine/state'
import {
  cancelPolicy$,
  isMember$,
  isPreMember$,
  plan$,
  reserveInfo$,
  transition$,
  useTransition,
  viewer$,
} from './stateMachine/state'
import type { NewCard, ReservationInputState, SelectedCard } from './stateMachine/statemachine'
import { isMobilePhoneNumber$, reserverName$, visitorName$ } from './userInput'

const isReservable$ = atom((get) => {
  const state = get(reservationInputStateValue$)
  return isReservable(state)
})
isReservable$.debugLabel = 'isReservable$'
const RESERVABLE_STATES = new Set<ReservationInputState['value']>([
  'RESERVABLE_UNTRUSTED_MEMBER',
  'RESERVABLE_TRUSTED_MEMBER',
  'RESERVABLE_UNTRUSTED_GUEST',
  'RESERVABLE_TRUSTED_GUEST',
  'RESERVABLE_PRE_MEMBER',
])
function isReservable(state: ReservationInputState['value']) {
  return RESERVABLE_STATES.has(state)
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  const ALL_STATES = (await import('./__tests__/stateValue')).RESERVATION_INPUT_STATES
  const NON_RESERVABLE_STATES = new Set([...ALL_STATES].filter((s) => !RESERVABLE_STATES.has(s)))
  it('isReservable', () => {
    for (const state of RESERVABLE_STATES) {
      expect(isReservable(state)).toBe(true)
    }
    for (const state of NON_RESERVABLE_STATES) {
      expect(isReservable(state)).toBe(false)
    }
  })
}
export function useIsReservable() {
  return useAtomValue(isReservable$)
}

const reserveButtonLabel$ = atom((get) => {
  return getReserveButtonLabel(get(isPreMember$), get(plan$))
})
reserveButtonLabel$.debugLabel = 'reserveButtonLabel$'

export function useReserveButtonLabel() {
  return useAtomValue(reserveButtonLabel$)
}

function getReserveButtonLabel(isPreMember: boolean, plan?: ReserveInputPlan) {
  if (plan?.isRequestReservation) {
    if (isPreMember) {
      return '会員登録してリクエスト予約を申し込む'
    }
    return 'リクエスト予約を申し込む'
  }
  return '予約を完了する'
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  describe('getReserveButtonText', () => {
    const isRequest = { isRequestReservation: true } as ReserveInputPlan
    const isNotRequest = { isRequestReservation: false } as ReserveInputPlan
    it('リクエスト予約の場合は文言を変更する', () => {
      expect(getReserveButtonLabel(true, isRequest)).toBe('会員登録してリクエスト予約を申し込む')
      expect(getReserveButtonLabel(false, isRequest)).toBe('リクエスト予約を申し込む')
    })
    it('リクエスト予約ではない場合は通常文言', () => {
      expect(getReserveButtonLabel(true, isNotRequest)).toBe('予約を完了する')
      expect(getReserveButtonLabel(false, isNotRequest)).toBe('予約を完了する')
    })
    it('plan が未定義の場合は通常文言', () => {
      expect(getReserveButtonLabel(true)).toBe('予約を完了する')
      expect(getReserveButtonLabel(false)).toBe('予約を完了する')
    })
  })
}

export function useSubmit() {
  const send = useTransition()
  return useCallback(() => {
    send({
      type: 'submitted',
    })
  }, [send])
}

const isAuthenticating$ = atom((get) => {
  const state = get(reservationInputStateValue$)
  return isAuthenticating(state)
})
isAuthenticating$.debugLabel = 'isAuthenticating$'
const AUTH_MODAL_STATES = new Set<ReservationInputState['value']>([
  'AUTH_MODAL_MEMBER',
  'AUTH_MODAL_GUEST',
  'AUTH_MODAL_PRE_MEMBER',
])
function isAuthenticating(state: ReservationInputState['value']) {
  return AUTH_MODAL_STATES.has(state)
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  const ALL_STATES = (await import('./__tests__/stateValue')).RESERVATION_INPUT_STATES
  const NON_AUTH_MODAL_STATES = new Set([...ALL_STATES].filter((s) => !AUTH_MODAL_STATES.has(s)))
  it('isAuthenticating', () => {
    for (const state of AUTH_MODAL_STATES) {
      expect(isAuthenticating(state)).toBe(true)
    }
    for (const state of NON_AUTH_MODAL_STATES) {
      expect(isAuthenticating(state)).toBe(false)
    }
  })
}

const authenticatorInput$ = atom((get) => {
  return {
    isMobileNumber: get(isMobilePhoneNumber$),
    phoneNumber: get(reserverPhoneNumber$) ?? '',
    email: get(email$) ?? '',
    showAuthModal: get(isAuthenticating$),
  }
})
authenticatorInput$.debugLabel = 'authenticatorInput$'
export function useAuthenticatorProps() {
  const send = useTransition()
  const close = useCallback(() => {
    send({ type: 'closed' })
  }, [send])
  const authenticate = useCallback(
    (key: string, authCode: string) => {
      send({ type: 'authenticated', result: { key, authCode } })
    },
    [send]
  )
  return {
    ...useAtomValue(authenticatorInput$),
    close,
    authenticate,
  }
}

const isLoggingIn$ = atom((get) => {
  const state = get(reservationInputStateValue$)
  return isLoggingIn(state)
})
isLoggingIn$.debugLabel = 'isLoggingIn$'

function useIsLoggingIn() {
  return useAtomValue(isLoggingIn$)
}

const LOGIN_MODAL_STATES = new Set<ReservationInputState['value']>([
  'LOGIN_MODAL_PRE_MEMBER',
  'LOGIN_MODAL_UNTRUSTED_GUEST',
  'LOGIN_MODAL_TRUSTED_GUEST',
])
function isLoggingIn(state: ReservationInputState['value']) {
  return LOGIN_MODAL_STATES.has(state)
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  const ALL_STATES = (await import('./__tests__/stateValue')).RESERVATION_INPUT_STATES
  const NON_LOGIN_MODAL_STATES = new Set([...ALL_STATES].filter((s) => !LOGIN_MODAL_STATES.has(s)))
  it('isLoggingIn', () => {
    for (const state of LOGIN_MODAL_STATES) {
      expect(isLoggingIn(state)).toBe(true)
    }
    for (const state of NON_LOGIN_MODAL_STATES) {
      expect(isLoggingIn(state)).toBe(false)
    }
  })
}

export function useLoginProps() {
  const send = useTransition()
  const signupOnly = useSignupOnly()
  const opened = useIsLoggingIn()
  const oauthProviderDestUrl = useRedirectBackUrlToReserveInput()
  const onClose = useCallback(() => {
    send({ type: 'closed' })
  }, [send])
  const onOpen = useCallback(
    (signupOnly: boolean) => {
      return () => {
        send({ type: 'loginModalOpened', signupOnly })
      }
    },
    [send]
  )
  const onLogin = useCallback(() => {
    send({ type: 'loggedIn' })
  }, [send])
  return {
    props: {
      opened,
      signupOnly,
      oauthProviderDestUrl,
      onClose,
      onLogin,
    },
    onOpen,
  }
}

const planInput$: Atom<ReservePlanInput | null> = atom((get) => {
  const plan = get(plan$)
  if (plan === undefined) {
    return null
  }
  return {
    planId: plan.planId,
    version: plan.version,
  }
})
planInput$.debugLabel = 'planInput$'

const reserver$: Atom<ReservePersonalInfoInput> = atom((get) => {
  return {
    name: get(reserverName$) ?? '',
    nameKana: get(reserverNameKana$) ?? '',
    phoneNumber: get(reserverPhoneNumber$) ?? '',
  }
})
reserver$.debugLabel = 'reserver$'

const visitor$: Atom<ReservePersonalInfoInput | null> = atom((get) => {
  if (!canChangeVisitor(get(isMember$), get(isReserverDifferentFromVisitor$))) {
    return null
  }
  return {
    name: get(visitorName$) ?? '',
    nameKana: get(visitorNameKana$) ?? '',
    phoneNumber: get(visitorPhoneNumber$) ?? '',
  }
})
visitor$.debugLabel = 'visitor$'

function canChangeVisitor(isMember: boolean, isReserverDifferentFromVisitor: boolean) {
  return isMember && isReserverDifferentFromVisitor
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('canChangeVisitor', () => {
    expect(canChangeVisitor(false, false)).toBe(false)
    expect(canChangeVisitor(false, true)).toBe(false)
    expect(canChangeVisitor(true, false)).toBe(false)
    expect(canChangeVisitor(true, true)).toBe(true)
  })
}

const cardInput$ = atom((get) => {
  return createCardInput(get(card$))
})
cardInput$.debugLabel = 'onlineSettlement$'

type CardInput =
  | { type: 'newCard'; cardId: null; cardToken: string }
  | { type: 'registeredCard'; cardId: string; cardToken: null }
  | { type: 'none'; cardId: null; cardToken: null }

function createCardInput(card?: SelectedCard): CardInput {
  if (card === undefined) {
    return { type: 'none', cardId: null, cardToken: null }
  }
  if (card.type === 'registered') {
    return { type: 'registeredCard', cardId: card.cardId, cardToken: null }
  } else if (card.type === 'new') {
    return { type: 'newCard', cardId: null, cardToken: card.newCard.token }
  }
  return { type: 'none', cardId: null, cardToken: null }
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('createCardInput', () => {
    expect(createCardInput()).toStrictEqual({ type: 'none', cardId: null, cardToken: null })
    expect(createCardInput({ type: 'registered', cardId: '1' })).toStrictEqual({
      type: 'registeredCard',
      cardId: '1',
      cardToken: null,
    })
    expect(createCardInput({ type: 'new', newCard: { token: 'token' } as NewCard })).toStrictEqual({
      type: 'newCard',
      cardId: null,
      cardToken: 'token',
    })
  })
}

const discountCouponInput$: Atom<ReserveDiscountCouponInput | null> = atom((get) => {
  const selected = get(discountCouponType$)
  const enabled = get(isDiscountCouponEnabled$)
  if (selected === undefined || !enabled || selected.code === undefined) {
    return null
  }
  return {
    amount: get(offPriceByCoupon$),
    code: selected.code,
  }
})
discountCouponInput$.debugLabel = 'discountCouponInput$'

const guestAuthenticationType$: Atom<ReserveGuestAuthenticationType> = atom((get) => {
  if (get(isMobilePhoneNumber$)) {
    return 'PHONE_NUMBER'
  }
  return 'EMAIL'
})
guestAuthenticationType$.debugLabel = 'guestAuthenticationType$'

const cancelPolicyInput$: Atom<ReserveCancelPolicyInput | null> = atom((get) => {
  const cancelPolicy = get(cancelPolicy$)
  if (
    cancelPolicy === null ||
    cancelPolicy === undefined ||
    cancelPolicy?.__typename === 'CancelPolicyText'
  ) {
    return null
  }
  return {
    cancelPolicyId: cancelPolicy.cancelPolicyId,
    version: cancelPolicy.version,
  }
})
cancelPolicyInput$.debugLabel = 'cancelPolicy$'

// ppg は必ず PayPay になる
const paypayGivenPointsTypeInput$: Atom<ReservationGivenPointsType> = atom('PAYPAY')
paypayGivenPointsTypeInput$.debugLabel = 'givenPointsTypeInput$'

const ikyuGivenPointsTypeInput$: Atom<ReservationGivenPointsType> = atom((get) => {
  return getGivenPointsTypeInput(
    get(selectedGivenPointsType$),
    get(selectedGivenPointsUsageForInput$),
    get(viewer$)
  )
})
ikyuGivenPointsTypeInput$.debugLabel = 'givenPointsTypeInput$'

function getGivenPointsTypeInput(
  type: ReservationGivenPointsType,
  usage: ReservationGivenPointsUsage,
  viewer?: ReserveInputViewer
) {
  // YID ログインの場合は即時利用有無に関わらず、必ず種別は PayPay になる
  if (viewer?.loginSite === LOGIN_SITE.Yahoo) {
    return 'PAYPAY'
  }
  // YID ログイン以外の場合で即時利用する場合、必ず種別は IKYU になる
  // ユーザーには表示しない
  // 即時利用される場合は、ユーザーがポイント種別を気にする必要がなくただポイントを使用して割引しているだけであるから
  if (usage === 'USE_INSTANTLY') {
    return 'IKYU'
  }
  return type
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  const ikyu = { loginSite: LOGIN_SITE.Ikyu } as ReserveInputViewer
  const yahoo = { loginSite: LOGIN_SITE.Yahoo } as ReserveInputViewer
  const otherSns = { loginSite: LOGIN_SITE.Line } as ReserveInputViewer
  it('getGivenPointsTypeInput', () => {
    expect(getGivenPointsTypeInput('IKYU', 'SAVE')).toBe('IKYU')
    expect(getGivenPointsTypeInput('IKYU', 'SAVE', ikyu)).toBe('IKYU')
    expect(getGivenPointsTypeInput('IKYU', 'SAVE', yahoo)).toBe('PAYPAY')
    expect(getGivenPointsTypeInput('IKYU', 'SAVE', otherSns)).toBe('IKYU')
    expect(getGivenPointsTypeInput('IKYU', 'USE_INSTANTLY', ikyu)).toBe('IKYU')
    expect(getGivenPointsTypeInput('IKYU', 'USE_INSTANTLY', yahoo)).toBe('PAYPAY')
    expect(getGivenPointsTypeInput('IKYU', 'USE_INSTANTLY', otherSns)).toBe('IKYU')
    expect(getGivenPointsTypeInput('PAYPAY', 'SAVE', ikyu)).toBe('PAYPAY')
    expect(getGivenPointsTypeInput('PAYPAY', 'SAVE', yahoo)).toBe('PAYPAY')
    expect(getGivenPointsTypeInput('PAYPAY', 'SAVE', otherSns)).toBe('PAYPAY')
    expect(getGivenPointsTypeInput('PAYPAY', 'USE_INSTANTLY', ikyu)).toBe('IKYU')
    expect(getGivenPointsTypeInput('PAYPAY', 'USE_INSTANTLY', yahoo)).toBe('PAYPAY')
    expect(getGivenPointsTypeInput('PAYPAY', 'USE_INSTANTLY', otherSns)).toBe('IKYU')
  })
}

const givenPointsTypeInput$ =
  import.meta.env.VITE_MODE === 'ppg' ? paypayGivenPointsTypeInput$ : ikyuGivenPointsTypeInput$

/** クレジットカードが使用できな場合は現地決済になるように調整した決済手段 */
const adjustedPaymentMethod$ = atom<ReservePaymentType>((get) => {
  const gmoMaintenance = get(reserveInfo$)?.gmoMaintenance ?? false
  // GMO メンテナンス中はカード決済できないので
  // 必ず現地決済になる
  if (gmoMaintenance) {
    return 'ONSITE'
  }
  return get(paymentMethod$)
})
adjustedPaymentMethod$.debugLabel = 'adjustedPaymentMethod$'

const reserveInput$: Atom<{ input: ReserveInput }> = atom((get) => {
  const planId = get(planId$)
  const seatId = get(seatId$)
  const visitDate = get(visitDate$)
  const visitTime = get(visitTime$)
  // context の型定義の都合上、初期化前に undefined を取り得る値であるため、ここでチェックしている
  // RESERVE_READY の段階で undefined になることはありえないが、誤った箇所で reserveInput$ atom が参照されたり、
  // 何かしらの不具合で undefined になったことを気付けるように Error を投げている
  if (
    planId === undefined ||
    seatId === undefined ||
    visitDate === undefined ||
    visitTime === undefined
  ) {
    throw new Error('invalid reserve input')
  }
  const restaurantId = get(restaurantId$)
  const plan = get(planInput$)
  const visitorsCount = get(visitorsCount$)
  const adults = get(adultCount$)
  const children = get(childCount$)
  const cancelPolicy = get(cancelPolicyInput$)
  const reserver = get(reserver$)
  const reserverMailAddress = get(email$)
  const visitor = get(visitor$)
  const totalAmount = get(totalAmount$)
  const paymentAmount = get(paymentAmount$)
  const paymentType = get(adjustedPaymentMethod$)
  const { cardId, cardToken } = get(cardInput$)
  const ikyuPointsUsed = get(appliedIkyuPoints$)
  const paypayBalanceUsed = get(appliedPaypayPoints$)
  const givenPoints = get(givenPoints$)
  const givenPointsUsage = get(selectedGivenPointsUsageForInput$)
  const givenPointsType = get(givenPointsTypeInput$)
  const discountCoupon = get(discountCouponInput$)
  const guestAuthenticationType = get(guestAuthenticationType$)
  const childMenus = get(selectedChildMenus$)
  const optionMenus = get(selectedOptionMenus$)
  const requestsAndComments = get(requestsAndComments$)
  const affiliateSiteFormAnswer = get(affiliateSiteFormAnswer$)
  const subscribeMailMagazineIkyuNews = get(subscribeMailMagazineIkyuNews$)
  const subscribeMailMagazinePointCoupon = get(subscribeMailMagazinePointCoupon$)

  return {
    input: {
      restaurantId,
      planId,
      plan,
      seatId,
      visitDate,
      visitTime,
      visitorsCount,
      adults,
      children,
      cancelPolicy,
      childMenus,
      optionMenus,
      reserver,
      reserverMailAddress,
      visitor,
      totalAmount,
      paymentAmount,
      paymentType,
      cardToken,
      cardId,
      ikyuPointsUsed,
      paypayBalanceUsed,
      givenPoints,
      givenPointsUsage,
      givenPointsType,
      discountCoupon,
      guestAuthenticationType,
      requestsAndComments,
      // 9:未設定 を固定にする
      // アンケート撤廃対応：https://github.com/ikyucom/macaron-frontend/issues/2695
      surveyAnswer: SURVEY_NOT_ANSWERED,
      subscribeMailMagazineIkyuNews,
      subscribeMailMagazinePointCoupon,
      affiliateSiteFormAnswer,
    } satisfies ReserveInput,
  }
})
reserveInput$.debugLabel = 'reserveInput$'

async function callReserve(
  variables: VariablesOf<typeof Reserve>
): Promise<ResultOf<typeof Reserve>> {
  const result = await fetch('/api/v2/reserve', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      variables,
    }),
  })
  if (!result.ok) {
    throw new Error(`Network error: ${result.status}`)
  }
  const json = await result.json()
  if ('errors' in json) {
    throw new Error(`GraphQL Error: ${JSON.stringify(json.errors)}`)
  }
  return json.data
}

async function reserve(variables: VariablesOf<typeof Reserve>) {
  const result = await callReserve(variables)
  if (result.reserve.__typename === 'ReserveOk') {
    return getFragmentData(ReserveSuccess, result.reserve)
  } else if (result.reserve.__typename === 'ReserveError') {
    if (isRecoverableReserveError(variables, result.reserve.messages)) {
      throw RecoverableClientError.fromHtmlFragments(result.reserve.messages)
    } else {
      throw ClientError.fromHtmlFragments(result.reserve.messages)
    }
  }
  throw new Error('unreachable')
}

function isRecoverableReserveError(
  variables: VariablesOf<typeof Reserve>,
  messages: HtmlFragment[]
) {
  // 「要望・コメント」に使用できない文字が含まれるエラーの場合は回復可とする
  if (variables.input.requestsAndComments) {
    // FIXME: エラー定義をコード化して判定方法を整理する
    const recoverableErrorCodes = [
      'E-RS-M-C10001', // 次の文字は使用できません
      'E-RS-C10005', // コメント欄には次の文字は使用できません
    ]
    return recoverableErrorCodes.some((code) => messages[0].includes(code))
  }
  return false
}

// ../specification.md 「atomEffect について」参照
export const listenReserveReady$ = atomEffect((get, set) => {
  const state = get(reservationInputStateValue$)
  if (state !== 'RESERVE_READY') {
    return
  }
  set.recurse(transition$, { type: 'started' })
  reserve(get(reserveInput$))
    .then((result) => {
      set.recurse(transition$, { type: 'succeeded', result })
    })
    .catch((error) => {
      // atomEffectWithError は非同期で throw したエラー処理に対応していないため、
      // ここでは直接 error$ にエラーをセットしている
      set(error$, error)
      set.recurse(transition$, { type: 'recovered' })
    })
})
listenReserveReady$.debugLabel = 'listenReserveReady$'

const registerMemberVariables$ = atomWithError((get) => {
  const auth = get(authResult$)
  const name = get(reserverName$)
  const nameKana = get(reserverNameKana$)
  const phoneNumber = get(reserverPhoneNumber$)
  const email = get(email$)
  const password = get(password$)
  if (
    auth === undefined ||
    name === undefined ||
    nameKana === undefined ||
    phoneNumber === undefined ||
    email === undefined ||
    password === undefined
  ) {
    throw new Error('invalid register member variables')
  }
  return {
    name,
    nameKana,
    phoneNumber,
    mailAddress: email,
    password,
    authCode: auth.authCode,
    key: auth.key,
  }
})
registerMemberVariables$.debugLabel = 'registerMemberVariables$'

// ../specification.md 「atomEffect について」参照
export const listenRegisteringMember$ = atomEffect((get, set) => {
  const state = get(reservationInputStateValue$)
  if (state !== 'REGISTERING_MEMBER') {
    return
  }
  const variables = get(registerMemberVariables$)
  if (variables === undefined) {
    return
  }
  callRegisterMember(get(env$).MEMBER_ORIGIN, variables)
    .then(() => {
      set.recurse(transition$, { type: 'registered' })
    })
    .catch((error) => {
      // atomEffectWithError は非同期で throw したエラー処理に対応していないため、
      // ここでは直接 error$ にエラーをセットしている
      set(error$, error)
    })
})

// ../specification.md 「atomEffect について」参照
export const listenCardRegistration$ = atomEffect((get, set) => {
  const state = get(reservationInputStateValue$)
  if (
    state !== 'CARD_REGISTRATION_UNTRUSTED_MEMBER' &&
    state !== 'CARD_REGISTRATION_TRUSTED_MEMBER' &&
    state !== 'CARD_REGISTRATION_UNTRUSTED_GUEST' &&
    state !== 'CARD_REGISTRATION_TRUSTED_GUEST' &&
    state !== 'CARD_REGISTRATION_PRE_MEMBER'
  ) {
    return
  }
  // キャンセルガードプランではない場合・カード情報選択済みの場合は、スキップして次の処理へ進む
  if (!get(plan$)?.isCancelGuard || get(cardInput$).type !== 'none') {
    set.recurse(transition$, { type: 'skipped' })
    return
  }
  set(createPayjpNewCardToken$)
    .then((newCard) => {
      set.recurse(transition$, {
        type: 'registeredCard',
        card: { type: 'new', newCard },
        userStatus: get(userStatus$),
      })
    })
    .catch((e) => {
      if (e instanceof Error) {
        set(tokenErrorOnReserve$, e.message)
      } else {
        error(e)
        set(
          tokenErrorOnReserve$,
          'カード情報の登録に失敗しました。入力内容をご確認の上、再度お試し下さい。'
        )
      }
      set.recurse(transition$, { type: 'failed' })
    })
})
